import { useState, useRef, useEffect, RefObject } from 'react';
import { generatePdfSnapshot } from '../../utils/pdfExportUtils';
import { exportReportAsCSV } from '../../utils/reportExport';
import type { AggregatedDataPoint, ReportConfiguration } from '../../types/reportTypes';
import '../../css/ReportExportMenu.css';

interface ReportExportMenuProps {
    data: AggregatedDataPoint[];
    config: ReportConfiguration;
    reportName: string;
    targetRef: RefObject<HTMLDivElement | null>;
}

/**
 * Export dropdown with PDF and CSV options.
 * PDF uses generatePdfSnapshot (react-to-pdf + print-friendly table styles).
 * CSV calls exportReportAsCSV from reportExport utility.
 *
 * Validates: Requirements 9.1, 9.2, 9.3, 9.4, 9.5
 */
export const ReportExportMenu = ({ data, config, reportName, targetRef }: ReportExportMenuProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handlePDFExport = () => {
        setIsOpen(false);
        // Let React remove the dropdown before html2canvas snapshots the target.
        window.setTimeout(() => {
            const getTarget = () => targetRef.current;
            const safeFilename = reportName.replace(/[^a-zA-Z0-9_-]/g, '_');
            void generatePdfSnapshot(getTarget, {
                filename: safeFilename,
            });
        }, 0);
    };

    const handleCSVExport = () => {
        setIsOpen(false);
        exportReportAsCSV(data, config, reportName);
    };

    return (
        <div className="report-export-wrapper" ref={wrapperRef} data-pdf-exclude>
            <button
                className="report-export-btn"
                onClick={() => setIsOpen((prev) => !prev)}
                aria-haspopup="true"
                aria-expanded={isOpen}
                aria-label="Export report"
            >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                Export
            </button>

            {isOpen && (
                <div className="report-export-dropdown" role="menu">
                    <button
                        className="report-export-option"
                        onClick={handlePDFExport}
                        role="menuitem"
                    >
                        <span className="report-export-option-icon">📄</span>
                        Export as PDF
                    </button>
                    <button
                        className="report-export-option"
                        onClick={handleCSVExport}
                        role="menuitem"
                    >
                        <span className="report-export-option-icon">📊</span>
                        Export as CSV
                    </button>
                </div>
            )}
        </div>
    );
};
